import { hoyLocal, fechaLocal } from '@/lib/fechas';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexto/AuthContexto';
import api, { formatoMXN, formatoFecha, ESTADOS, PRIORIDADES, METODOS_PAGO } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import FotosSubir from '@/componentes/FotosSubir';
import {
  ArrowLeft, User, Car, Wrench, DollarSign, Calendar, Plus,
  Pencil, AlertCircle, CheckCircle, Trash2, Printer, FileDown, MessageCircle, Search, Package
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const flujoEstados = ['recibido', 'diagnostico', 'reparando', 'terminado', 'entregado'];

export default function OrdenDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const esMecanico = usuario?.rol === 'mecanico';
  const [orden, setOrden] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [pagoDialog, setPagoDialog] = useState(false);
  const [pagoForm, setPagoForm] = useState({ monto: '', metodo_pago: 'efectivo', referencia: '', notas: '', fecha_pago: hoyLocal() });
  const [guardandoPago, setGuardandoPago] = useState(false);
  // Mechanic add part
  const [insumoDialog, setInsumoDialog] = useState(false);
  const [insumoDesc, setInsumoDesc] = useState('');
  const [insumoCant, setInsumoCant] = useState(1);
  const [insumoInvId, setInsumoInvId] = useState('');
  const [inventarioItems, setInventarioItems] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guardandoInsumo, setGuardandoInsumo] = useState(false);
  const [config, setConfig] = useState(null);

  const getWhatsAppUrl = () => {
    const tel = orden?.cliente_telefono || '';
    const telFmt = tel.replace(/\D/g, '');
    const estado = ESTADOS[orden?.estado]?.label || orden?.estado;
    const msg = `Hola! Le informamos que su vehiculo ${orden?.vehiculo_info || ''} esta en estatus: *${estado}*. Orden: ${orden?.numero_orden}. Talleres PRO.`;
    const encoded = encodeURIComponent(msg);
    const ua = navigator.userAgent;
    const isIPad = /iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    if (isIPad) return `https://web.whatsapp.com/send?phone=${telFmt}&text=${encoded}`;
    return `https://api.whatsapp.com/send?phone=${telFmt}&text=${encoded}`;
  };

  const cargar = async () => {
    try {
      const res = await api.get(`/ordenes/${id}`);
      setOrden(res.data);
    } catch { toast.error('Error al cargar orden'); navigate('/ordenes'); }
    finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, [id]); // eslint-disable-line
  useEffect(() => {
    api.get('/configuracion').then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const cambiarEstado = async (nuevoEstado) => {
    try {
      await api.patch(`/ordenes/${id}/estado`, { estado: nuevoEstado });
      toast.success(`Estado: ${ESTADOS[nuevoEstado]?.label}`);
      cargar();
    } catch { toast.error('Error al cambiar estado'); }
  };

  const registrarPago = async () => {
    if (!pagoForm.monto || parseFloat(pagoForm.monto) <= 0) { toast.error('Ingresa un monto valido'); return; }
    setGuardandoPago(true);
    try {
      await api.post('/pagos', { orden_id: id, ...pagoForm, monto: parseFloat(pagoForm.monto) });
      toast.success('Pago registrado');
      setPagoDialog(false);
      setPagoForm({ monto: '', metodo_pago: 'efectivo', referencia: '', notas: '', fecha_pago: hoyLocal() });
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al registrar pago'); }
    finally { setGuardandoPago(false); }
  };

  const eliminarPago = async (pagoId) => {
    try {
      await api.delete(`/pagos/${pagoId}`);
      toast.success('Pago eliminado');
      cargar();
    } catch { toast.error('Error al eliminar pago'); }
  };

  // Mechanic: search inventory and add part
  const buscarInventarioInsumo = async (q) => {
    setInsumoDesc(q);
    try {
      const res = await api.get(`/inventario/buscar?q=${encodeURIComponent(q)}`);
      setInventarioItems(res.data);
      setDropdownOpen(true);
    } catch { setInventarioItems([]); }
  };

  const seleccionarInsumoInventario = (prod) => {
    setInsumoDesc(prod.nombre);
    setInsumoInvId(prod.id);
    setDropdownOpen(false);
  };

  const agregarInsumo = async () => {
    if (!insumoDesc.trim()) { toast.error('Escribe la descripcion del insumo'); return; }
    setGuardandoInsumo(true);
    try {
      await api.post(`/ordenes/${id}/agregar-insumo`, {
        descripcion: insumoDesc.trim(),
        cantidad: insumoCant,
        inventario_id: insumoInvId,
      });
      toast.success('Insumo agregado (pendiente de precio)');
      setInsumoDialog(false);
      setInsumoDesc('');
      setInsumoCant(1);
      setInsumoInvId('');
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al agregar insumo'); }
    finally { setGuardandoInsumo(false); }
  };

  if (cargando) return <div className="space-y-4"><Skeleton className="h-12 w-full bg-slate-800" /><Skeleton className="h-64 w-full bg-slate-800" /></div>;
  if (!orden) return <div className="text-center py-12"><AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" /><p className="text-slate-400">Orden no encontrada</p></div>;

  const estadoIdx = flujoEstados.indexOf(orden.estado);
  const puedeAgregarInsumos = esMecanico && config?.mecanico_puede_agregar_insumos;

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="orden-detalle-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes')} className="text-slate-400 hover:text-white"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="font-barlow text-2xl font-bold uppercase tracking-tight text-slate-50">{orden.numero_orden}</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <Badge className={`${ESTADOS[orden.estado]?.color || 'bg-slate-700 text-slate-300'} text-xs`}>{ESTADOS[orden.estado]?.label}</Badge>
              <Badge className={`${PRIORIDADES[orden.prioridad]?.color || 'bg-slate-700'} text-xs`}>{PRIORIDADES[orden.prioridad]?.label}</Badge>
            </div>
          </div>
        </div>
        {!esMecanico && (
          <div className="flex items-center gap-2">
            <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-green-600 hover:bg-green-700 text-white text-xs font-semibold transition-colors"
              data-testid="enviar-whatsapp-btn">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <Button variant="ghost" size="icon" onClick={() => navigate(`/ordenes/${id}/editar`)} className="text-slate-400 hover:text-amber-400" data-testid="editar-orden-btn"><Pencil className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" onClick={() => {
              const t = localStorage.getItem('token') || '';
              window.open(`${API_URL}/api/ordenes/${id}/pdf?token=${t}`, '_blank');
            }} className="text-slate-400 hover:text-amber-400" data-testid="descargar-pdf-btn"><FileDown className="w-4 h-4" /></Button>
            <Button variant="ghost" size="icon" onClick={() => navigate(`/ordenes/${id}/imprimir`)} className="text-slate-400 hover:text-amber-400" data-testid="imprimir-orden-btn"><Printer className="w-4 h-4" /></Button>
          </div>
        )}
      </div>

      {/* Status flow - mechanic CAN change status */}
      {orden.estado !== 'cancelado' && (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {flujoEstados.map((est, i) => {
                const esActual = est === orden.estado;
                const esPasado = i < estadoIdx;
                return (
                  <button key={est} onClick={() => !esActual && cambiarEstado(est)} disabled={esActual}
                    className={`flex-1 min-w-[100px] px-3 py-2 rounded-sm text-xs font-semibold text-center transition-colors ${
                      esActual ? 'bg-amber-500 text-black' :
                      esPasado ? 'bg-slate-800 text-green-400' :
                      'bg-slate-800/50 text-slate-500 hover:bg-slate-700 hover:text-slate-300 cursor-pointer'
                    }`} data-testid={`estado-btn-${est}`}>
                    {ESTADOS[est]?.label}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className={`${esMecanico ? 'lg:col-span-3' : 'lg:col-span-2'} space-y-4`}>
          {/* Order details */}
          <Card className="bg-slate-900/40 border-slate-800">
            <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Detalles</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-3 bg-slate-800/40 rounded-sm border border-slate-800">
                  <p className="text-xs text-slate-500 uppercase mb-1 flex items-center gap-1"><User className="w-3 h-3" /> Cliente</p>
                  {!esMecanico ? (
                    <button onClick={() => navigate(`/clientes/${orden.cliente_id}`)} className="text-sm text-amber-500 hover:text-amber-400" data-testid="ver-cliente-btn">
                      {orden.cliente_nombre || 'Sin cliente'}
                    </button>
                  ) : <p className="text-sm text-slate-300">{orden.cliente_nombre || 'Sin cliente'}</p>}
                </div>
                <div className="p-3 bg-slate-800/40 rounded-sm border border-slate-800">
                  <p className="text-xs text-slate-500 uppercase mb-1 flex items-center gap-1"><Car className="w-3 h-3" /> Vehiculo</p>
                  {!esMecanico ? (
                    <button onClick={() => navigate(`/vehiculos/${orden.vehiculo_id}`)} className="text-sm text-amber-500 hover:text-amber-400" data-testid="ver-vehiculo-btn">
                      {orden.vehiculo_info || 'Sin vehiculo'}
                    </button>
                  ) : <p className="text-sm text-slate-300">{orden.vehiculo_info || 'Sin vehiculo'}</p>}
                </div>
                <div className="p-3 bg-slate-800/40 rounded-sm border border-slate-800">
                  <p className="text-xs text-slate-500 uppercase mb-1 flex items-center gap-1"><Wrench className="w-3 h-3" /> Mecanico</p>
                  <p className="text-sm text-slate-300">{orden.mecanico_nombre}</p>
                </div>
                <div className="p-3 bg-slate-800/40 rounded-sm border border-slate-800">
                  <p className="text-xs text-slate-500 uppercase mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Fecha Promesa</p>
                  <p className="text-sm text-slate-300">{formatoFecha(orden.fecha_promesa) || 'Sin definir'}</p>
                </div>
              </div>
              <div><p className="text-xs text-slate-500 uppercase mb-1">Problema Reportado</p><p className="text-sm text-slate-300 bg-slate-800/40 p-3 rounded-sm border border-slate-800">{orden.problema_reportado || 'Sin descripcion'}</p></div>
              {orden.diagnostico && <div><p className="text-xs text-slate-500 uppercase mb-1">Diagnostico</p><p className="text-sm text-slate-300 bg-slate-800/40 p-3 rounded-sm border border-slate-800">{orden.diagnostico}</p></div>}
              {orden.reparacion_realizada && <div><p className="text-xs text-slate-500 uppercase mb-1">Reparacion Realizada</p><p className="text-sm text-slate-300 bg-slate-800/40 p-3 rounded-sm border border-slate-800">{orden.reparacion_realizada}</p></div>}
              {orden.notas_internas && !esMecanico && <div><p className="text-xs text-slate-500 uppercase mb-1">Notas Internas</p><p className="text-sm text-slate-400 bg-slate-800/40 p-3 rounded-sm border border-slate-800">{orden.notas_internas}</p></div>}
            </CardContent>
          </Card>

          {/* Parts list - mechanic sees names only, no prices */}
          {orden.partes?.length > 0 && (
            <Card className="bg-slate-900/40 border-slate-800">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-barlow text-lg text-slate-200 flex items-center gap-2"><Package className="w-5 h-5 text-amber-500" /> Refacciones e Insumos</CardTitle>
                  {puedeAgregarInsumos && (
                    <Button size="sm" onClick={() => { setInsumoDialog(true); buscarInventarioInsumo(''); }} className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm" data-testid="agregar-insumo-btn">
                      <Plus className="w-3 h-3 mr-1" /> Agregar
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {orden.partes.map((p, i) => (
                    <div key={p.id || i} className={`flex items-center justify-between p-2 rounded-sm border ${p.pendiente_precio ? 'bg-amber-500/5 border-amber-500/20' : 'bg-slate-800/40 border-slate-800'}`}>
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-sm text-slate-300">{p.cantidad}x</span>
                        <span className="text-sm text-slate-200">{p.descripcion}</span>
                        {p.pendiente_precio && <Badge className="text-[10px] bg-amber-500/20 text-amber-400 border-amber-800">Pendiente precio</Badge>}
                        {p.agregado_por_nombre && <span className="text-[10px] text-slate-600">por {p.agregado_por_nombre}</span>}
                      </div>
                      {!esMecanico && <span className="text-sm text-amber-500 font-mono">{formatoMXN(p.subtotal)}</span>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Mechanic: show add button even if no parts yet */}
          {puedeAgregarInsumos && (!orden.partes || orden.partes.length === 0) && (
            <Card className="bg-slate-900/40 border-slate-800">
              <CardContent className="p-6 text-center">
                <p className="text-sm text-slate-500 mb-3">Sin refacciones agregadas</p>
                <Button size="sm" onClick={() => { setInsumoDialog(true); buscarInventarioInsumo(''); }} className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm" data-testid="agregar-insumo-empty-btn">
                  <Plus className="w-3 h-3 mr-1" /> Agregar Insumo
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Financial sidebar - HIDDEN for mechanic */}
        {!esMecanico && (
          <div className="space-y-4">
            <Card className="bg-slate-900/40 border-slate-800">
              <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200 flex items-center gap-2"><DollarSign className="w-5 h-5 text-amber-500" /> Resumen Financiero</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Mano de obra</span><span className="text-slate-300 font-mono">{formatoMXN(orden.costo_mano_obra)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Refacciones ({orden.partes?.length || 0})</span><span className="text-slate-300 font-mono">{formatoMXN(orden.costo_partes)}</span></div>
                {orden.partes?.length > 0 && (
                  <div className="ml-2 space-y-1 border-l-2 border-slate-800 pl-3">
                    {orden.partes.map((p, i) => (
                      <div key={p.id || i} className="flex justify-between text-xs">
                        <span className="text-slate-500 truncate mr-2">{p.cantidad}x {p.descripcion}</span>
                        <span className="text-slate-400 font-mono whitespace-nowrap">{formatoMXN(p.subtotal)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex justify-between text-sm border-t border-slate-800 pt-2"><span className="text-slate-500">Subtotal</span><span className="text-slate-300 font-mono">{formatoMXN(orden.subtotal)}</span></div>
                {orden.descuento > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">Descuento</span><span className="text-green-400 font-mono">-{formatoMXN(orden.descuento)}</span></div>}
                {orden.incluir_iva && orden.iva > 0 && <div className="flex justify-between text-sm"><span className="text-slate-500">{orden.nombre_impuesto || 'IVA'} {(orden.tasa_impuesto ?? 16)}%</span><span className="text-slate-300 font-mono">{formatoMXN(orden.iva)}</span></div>}
                <div className="flex justify-between text-sm font-bold border-t border-slate-800 pt-2"><span className="text-slate-200">Total</span><span className="text-amber-500 font-mono">{formatoMXN(orden.total)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Pagado</span><span className="text-green-400 font-mono">{formatoMXN(orden.total_pagado)}</span></div>
                <div className="flex justify-between text-sm font-bold border-t border-slate-800 pt-2">
                  <span className="text-slate-200">Saldo Pendiente</span>
                  <span className={`font-mono ${orden.saldo_pendiente > 0 ? 'text-orange-400' : 'text-green-400'}`}>{formatoMXN(orden.saldo_pendiente)}</span>
                </div>
                {orden.saldo_pendiente > 0 && (
                  <Button onClick={() => setPagoDialog(true)} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm mt-2" data-testid="registrar-pago-btn">
                    <Plus className="w-4 h-4 mr-2" /> Registrar Pago
                  </Button>
                )}
                {orden.saldo_pendiente === 0 && orden.total > 0 && (
                  <div className="text-center py-2"><span className="text-green-400 text-sm flex items-center justify-center gap-1"><CheckCircle className="w-4 h-4" /> Pagado completo</span></div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-slate-900/40 border-slate-800">
              <CardHeader className="pb-3"><CardTitle className="font-barlow text-lg text-slate-200">Historial de Pagos</CardTitle></CardHeader>
              <CardContent>
                {orden.pagos?.length > 0 ? (
                  <div className="space-y-2">
                    {orden.pagos.map(p => (
                      <div key={p.id} className="p-3 bg-slate-800/40 rounded-sm border border-slate-800 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-amber-500 font-mono font-bold">{formatoMXN(p.monto)}</span>
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className="text-slate-400 border-slate-700 text-xs">{p.metodo_pago}</Badge>
                            <Button variant="ghost" size="icon" onClick={() => navigate(`/ordenes/${id}/recibo/${p.id}`)} className="text-slate-500 hover:text-amber-400 h-6 w-6" data-testid={`imprimir-recibo-${p.id}`}><Printer className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => eliminarPago(p.id)} className="text-slate-500 hover:text-red-400 h-6 w-6" data-testid={`eliminar-pago-${p.id}`}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{formatoFecha(p.fecha_pago)} {p.referencia && `- ${p.referencia}`}</p>
                        {p.notas && <p className="text-xs text-slate-600 mt-1">{p.notas}</p>}
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-slate-500 text-center py-4">Sin pagos registrados</p>}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Fotos de recepción del vehículo + evidencias del trabajo */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <FotosSubir tipo="recepcion_orden" entidadId={orden.id}
          titulo="Fotos de recepción del vehículo" maxFotos={8} />
        <FotosSubir tipo="evidencia_orden" entidadId={orden.id}
          titulo="Evidencias del trabajo realizado" maxFotos={12} />
      </div>

      {/* Payment dialog - only non-mechanic */}
      {!esMecanico && (
        <Dialog open={pagoDialog} onOpenChange={setPagoDialog}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="pago-dialog">
            <DialogHeader><DialogTitle className="font-barlow text-xl text-slate-100">Registrar Pago</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-slate-800/40 rounded-sm border border-slate-800">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Saldo pendiente:</span><span className="text-orange-400 font-mono font-bold">{formatoMXN(orden.saldo_pendiente)}</span></div>
              </div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Fecha del Pago *</Label>
                <Input type="date" value={pagoForm.fecha_pago}
                  onChange={e => setPagoForm({...pagoForm, fecha_pago: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-slate-200"
                  data-testid="pago-fecha-input" />
                <p className="text-[10px] text-slate-500 mt-1">Fecha en que el cliente entregó el dinero. Se usa para el reporte semanal.</p>
              </div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Monto *</Label>
                <Input type="number" step="0.01" value={pagoForm.monto} onChange={e => setPagoForm({...pagoForm, monto: e.target.value})}
                  placeholder={`Max: ${orden.saldo_pendiente}`} className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="pago-monto-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Metodo de Pago</Label>
                <Select value={pagoForm.metodo_pago} onValueChange={v => setPagoForm({...pagoForm, metodo_pago: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="pago-metodo-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {METODOS_PAGO.map(m => <SelectItem key={m.valor} value={m.valor} className="text-slate-300">{m.label}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Referencia</Label>
                <Input value={pagoForm.referencia} onChange={e => setPagoForm({...pagoForm, referencia: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-slate-200" data-testid="pago-referencia-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Notas</Label>
                <Textarea value={pagoForm.notas} onChange={e => setPagoForm({...pagoForm, notas: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-slate-200" rows={2} data-testid="pago-notas-input" /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setPagoDialog(false)} className="text-slate-400">Cancelar</Button>
              <Button onClick={registrarPago} disabled={guardandoPago} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="confirmar-pago-btn">
                {guardandoPago ? 'Registrando...' : 'Registrar Pago'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Mechanic add part dialog */}
      <Dialog open={insumoDialog} onOpenChange={setInsumoDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="insumo-dialog">
          <DialogHeader><DialogTitle className="font-barlow text-xl text-slate-100">Agregar Insumo / Refaccion</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Producto *</Label>
              <div className="relative" data-inv-dropdown="true">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
                  <Input value={insumoDesc}
                    onChange={e => buscarInventarioInsumo(e.target.value)}
                    onFocus={() => buscarInventarioInsumo(insumoDesc)}
                    placeholder="Buscar en inventario..."
                    className="bg-slate-950 border-slate-700 text-slate-200 text-sm pl-7"
                    data-testid="insumo-desc-input" />
                </div>
                {dropdownOpen && inventarioItems.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-slate-900 border border-slate-700 rounded-md shadow-xl max-h-48 overflow-y-auto" data-inv-dropdown="true">
                    {inventarioItems.map(prod => (
                      <button key={prod.id} onMouseDown={e => e.preventDefault()} onClick={() => seleccionarInsumoInventario(prod)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-800 flex items-center justify-between gap-2 text-sm">
                        <span className="text-slate-200 truncate">{prod.nombre}</span>
                        <span className={`text-xs ${prod.stock > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{prod.stock} disp.</span>
                      </button>
                    ))}
                    <button onMouseDown={e => e.preventDefault()} onClick={() => { setInsumoInvId(''); setDropdownOpen(false); }}
                      className="w-full text-left px-3 py-2 hover:bg-slate-800 text-sm border-t border-slate-700 text-indigo-400">
                      + Otro (manual)
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Cantidad</Label>
              <Input type="number" min="1" step="1" value={insumoCant} onChange={e => setInsumoCant(parseInt(e.target.value) || 1)}
                className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="insumo-cant-input" /></div>
            <p className="text-[10px] text-slate-500">El precio sera asignado por el personal administrativo.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInsumoDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={agregarInsumo} disabled={guardandoInsumo} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="confirmar-insumo-btn">
              {guardandoInsumo ? 'Agregando...' : 'Agregar Insumo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
